import { ReactNode, useMemo } from "react"

import styled, { CSSProp } from "styled-components"

import Button from "components/buttons/Button"
import LinkButton from "components/buttons/LinkButton"
import RoundButton from "components/buttons/RoundButton"
import Kicker, { KickerTextType } from "components/TextElements/Kicker"
import ToolTip from "components/ToolTip"
import colors from "styles/colors"
import media from "styles/media"
import textStyles from "styles/text"
import { ColorType, ButtonProps } from "types"

interface ToolTipProps {
  text: string
  position: string
}

interface KickerProps {
  text?: string
  icon?: KickerTextType
  alternateStyle?: boolean
  color?: ColorType
  gradient?: string
}

interface TextProps {
  content: ReactNode
  width?: string
  offset?: boolean
  size?: (typeof textStyles)[keyof typeof textStyles] | null
  color?: ColorType | undefined
}

interface TitleProps {
  content: string | null | ReactNode
  width?: string
  offset?: boolean
  size?: (typeof textStyles)[keyof typeof textStyles] | null
  color?: ColorType | undefined
}

export type VariantType =
  | "default"
  | "lightmode"
  | "white"
  | "blue800"
  | "darkMode"

export interface Props {
  variant?: VariantType
  title: TitleProps
  text?: TextProps
  kicker?: KickerProps
  centered?: boolean
  left?: boolean
  flex?: boolean
  stacked?: boolean
  topAlign?: boolean
  buttons?: ButtonProps[]
  className?: string
  toolTip?: ToolTipProps
}

export default function Callout({
  variant = "default",
  text = undefined,
  title,
  kicker = undefined,
  centered = false,
  left = false,
  flex = false,
  stacked = false,
  topAlign = false,
  buttons = undefined,
  className = "",
  toolTip = undefined,
}: Props) {
  const variants: {
    [key in VariantType]: {
      title: { color: ColorType | undefined }
      text: { color: ColorType | undefined }
    }
  } = {
    default: {
      title: { color: colors.blue300 },
      text: { color: colors.blue10 },
    },
    lightmode: {
      title: { color: colors.blue800 },
      text: { color: colors.blue200 },
    },
    white: {
      title: { color: colors.white },
      text: { color: colors.blue50 },
    },
    darkMode: {
      title: { color: colors.white },
      text: { color: colors.blue10 },
    },
    blue800: {
      title: { color: colors.blue800 },
      text: { color: colors.blue800 },
    },
  }

  const stylesToUse: {
    title: { color: ColorType | undefined }
    text: { color: ColorType | undefined }
    lightmode?: boolean
  } = variants[variant]

  //  this is where to override the variant with optional custom colors if they are added in the title or text prop
  if (title.color !== undefined) {
    stylesToUse.title.color = title.color
  }
  if (text?.color !== undefined) {
    stylesToUse.text.color = text.color
  }

  const buttonEls = useMemo(() => {
    if (buttons) {
      const allButtons = buttons.map(item => {
        let button

        if (item)
          if (item.type === "link") {
            button = (
              <LinkButton
                key={JSON.stringify(item)}
                gradient={item.gradient}
                link={item.link}
              >
                {item.text}
              </LinkButton>
            )
          } else if (item.type === "round") {
            button = (
              <RoundButton key={JSON.stringify(item)} onClick={item.onClick} />
            )
          } else
            button = (
              <Button
                key={item.text ?? "empty button"}
                onClick={item.onClick}
                download={item.download}
                arrow={item.arrow}
                styledLink={!!item.route}
                secondary={item.type === "secondary"}
                lightMode={
                  item.lightMode ? item.lightMode : variant === "lightmode"
                }
                rotate={item.rotate}
                href={item.route}
              >
                {item.text}
              </Button>
            )

        return button
      })
      return allButtons
    }
  }, [buttons, variant])

  return (
    <CalloutWrapper
      className={className}
      centered={centered}
      left={left}
      topAlign={topAlign}
      key="unique"
    >
      <Text centered={centered} flex={flex}>
        <ContentWrap centered={centered}>
          {kicker && (
            <Kicker
              kickerText={kicker.text}
              kickerIcon={kicker.icon}
              centered={centered}
              small={kicker.alternateStyle}
              gradient={kicker.gradient}
              color={kicker.color}
            />
          )}
          <Title
            centered={centered}
            titleWidth={title.width}
            size={title.size}
            titleOffset={title.offset}
            color={stylesToUse.title.color}
            noText={!text}
          >
            {title.content}
            {toolTip && (
              <ToolTipWrapper>
                <ToolTip
                  bannerText={toolTip.text}
                  position={toolTip.position}
                />
              </ToolTipWrapper>
            )}
          </Title>
        </ContentWrap>
        <ContentWrap centered={centered}>
          {text && (
            <Body
              size={text.size}
              centered={centered}
              textWidth={text.width}
              color={stylesToUse.text.color}
            >
              {text.content}
            </Body>
          )}

          {buttons?.length && (
            <ButtonRow
              noText={!text}
              centered={centered}
              stacked={stacked}
              left={left}
            >
              {buttonEls}
            </ButtonRow>
          )}
        </ContentWrap>
      </Text>
    </CalloutWrapper>
  )
}

const CalloutWrapper = styled.div<{
  centered?: boolean
  left?: boolean
  topAlign?: boolean
}>`
  height: auto;
  display: flex;
  position: relative;
  z-index: 2;
  align-items: ${({ topAlign }) => (topAlign ? "flex-start" : "center")};
  justify-content: ${({ centered, left }) =>
    left ? "flex-start" : centered ? "center" : "space-between"};
  ${media.mobile} {
    margin-bottom: 50px;
  }
`

const Text = styled.div<{
  flex?: boolean
  centered?: boolean
  textAlign?: string
}>`
  position: relative;

  height: auto;
  display: flex;
  flex-direction: column;
  text-align: ${({ centered }) => centered && "center"};
  justify-content: ${props => (props.centered ? "center" : "flex-start")};
  align-items: ${props => (props.centered ? "center" : "flex-start")};
  ${({ flex }) =>
    flex &&
    `
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  `}
  color: ${colors.blue300};
`

const ContentWrap = styled.div<{ centered?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${props => (props.centered ? "center" : "left")};
  width: fit-content;
  ${media.mobile} {
    width: 100%;
  }
`

const Title = styled.h3<{
  titleWidth?: string
  centered?: boolean
  size?: CSSProp | null
  titleOffset?: boolean
  color?: string
  noText: boolean
}>`
  ${props => (props.size ? props.size : textStyles.h2)};
  color: ${props => (props.color ? props.color : colors.white)};
  text-align: ${props => (props.centered ? "center" : "left")};

  position: relative;
  z-index: 2;

  ${media.fullWidth} {
    margin-bottom: ${({ noText }) => (noText ? "30px" : "20px")};
    margin-left: ${({ titleOffset }) => (titleOffset ? "-80px" : "0")};
    width: ${props => (props.titleWidth ? props.titleWidth : "627px")};
  }

  ${media.desktop} {
    margin-bottom: ${({ noText }) => (noText ? "1.807vw" : "1.2vw")};
    margin-left: ${({ titleOffset }) => (titleOffset ? "-4.82vw" : "0")};
    width: ${props => (props.titleWidth ? props.titleWidth : "37.77vw")};
  }

  ${media.tablet} {
    margin-left: ${({ titleOffset }) => (titleOffset ? "7.813vw" : "0")};
    width: ${props => (props.titleWidth ? props.titleWidth : "47.363vw")};
    margin-bottom: ${({ noText }) => (noText ? "2.93vw" : "1.953vw")};
  }

  ${media.mobile} {
    ${props => (props.size ? props.size : textStyles.h1Mobile)};
    width: ${props => (props.titleWidth ? props.titleWidth : "47.363vw")};
    margin-bottom: ${({ noText }) => (noText ? "5.333vw" : "2.667vw")};
  }
`

const ToolTipWrapper = styled.div`
  display: inline-block;
  align-items: center;
  justify-content: center;
  position: relative;

  ${media.fullWidth} {
    top: -10px;
    left: 5px;
  }

  ${media.desktop} {
    top: -0.6vw;
    left: 0.3vw;
  }

  ${media.tablet} {
    top: -0.29vw;
  }
`

const Body = styled.div<{
  textWidth?: string
  color?: string
  centered?: boolean
  buttonType?: string
  size?: CSSProp | null
}>`
  ${props => (props.size ? props.size : textStyles.bodyR)};
  color: ${props => (props.color ? props.color : colors.blue10)};
  text-align: ${props => (props.centered ? "center" : "left")};
  white-space: pre-wrap;
  ${({ size }) => size ?? textStyles.bodyR};

  a {
    text-decoration: underline;
  }
  ${media.fullWidth} {
    width: ${props => (props.textWidth ? props.textWidth : "fit-content")};
  }

  ${media.desktop} {
    width: ${props => (props.textWidth ? props.textWidth : "fit-content")};
  }

  ${media.tablet} {
    width: ${props => (props.textWidth ? props.textWidth : "47.363vw")};
    margin: unset;
  }

  ${media.mobile} {
    ${props => (props.size ? props.size : textStyles.bodyR)};
    width: ${props => (props.textWidth ? props.textWidth : "89.333vw")};
  }
`

const ButtonRow = styled.div<{
  centered?: boolean
  left?: boolean
  stacked?: boolean
  noText: boolean
}>`
  display: flex;
  flex-direction: ${({ stacked }) => (stacked ? "column" : "row")};
  justify-content: ${({ centered }) => (centered ? "center" : "flex-start")};
  align-items: center;

  ${media.fullWidth} {
    margin-top: ${({ noText }) => (noText ? "0" : "40px")};
    gap: 20px;
  }

  ${media.desktop} {
    margin-top: ${({ noText }) => (noText ? "0" : "2.41vw")};
    gap: 1.2vw;
  }

  ${media.tablet} {
    margin-top: ${({ noText }) => (noText ? "0" : "3.906vw")};
    gap: 1.95vw;
  }

  ${media.mobile} {
    gap: 5.333vw;
    margin-top: ${({ noText }) => (noText ? "0" : "3.906vw")};
    width: 89.333vw;
  }
`
